import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navigation from './components/Navigation';
import Hero from './sections/Hero';
import ContactCTA from './sections/ContactCTA';
import SovereignShipment from './sections/SovereignShipment';
import GrainOverlay from './components/GrainOverlay';
import { useHealth } from './hooks/useApi';

gsap.registerPlugin(ScrollTrigger);

function App() {
  const mainRef = useRef<HTMLDivElement>(null);
  const { data: health, error: healthError } = useHealth();

  useEffect(() => {
    // Wait for all sections to mount and create their ScrollTriggers
    const timer = setTimeout(() => {
      const pinned = ScrollTrigger.getAll()
        .filter(st => st.vars.pin)
        .sort((a, b) => a.start - b.start);
      
      const maxScroll = ScrollTrigger.maxScroll(window);
      
      if (!maxScroll || pinned.length === 0) return;

      // Build ranges and snap targets from pinned sections
      const pinnedRanges = pinned.map(st => ({
        start: st.start / maxScroll,
        end: (st.end ?? st.start) / maxScroll,
        center: (st.start + ((st.end ?? st.start) - st.start) * 0.5) / maxScroll,
      }));

      // Global snap implementation
      ScrollTrigger.create({
        snap: {
          snapTo: (value: number) => {
            // Check if within any pinned range (with small buffer)
            const inPinned = pinnedRanges.some(
              r => value >= r.start - 0.02 && value <= r.end + 0.02
            );
            
            if (!inPinned) return value; // Flowing section: free scroll

            // Find nearest pinned center
            const target = pinnedRanges.reduce(
              (closest, r) =>
                Math.abs(r.center - value) < Math.abs(closest - value)
                  ? r.center
                  : closest,
              pinnedRanges[0]?.center ?? 0
            );

            return target;
          },
          duration: { min: 0.15, max: 0.35 },
          delay: 0,
          ease: 'power2.out',
        },
      });
    }, 500);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(st => st.kill());
    };
  }, []);

  // Show connection status
  const isConnected = health?.status === 'healthy';

  return (
    <div ref={mainRef} className="relative bg-synapse-black">
      <Navigation />
      <GrainOverlay />
      
      {/* Connection Status Banner */}
      {healthError && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-synapse-red/90 text-white px-4 py-2 rounded-lg text-sm">
          (connection temporarily unavailable)
        </div>
      )}
      
      {isConnected && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2 bg-synapse-graphite/90 border border-synapse-gold/30 px-3 py-1.5 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-synapse-gold animate-signal-pulse" />
          <span className="text-xs font-mono text-synapse-gold">(attentive)</span>
        </div>
      )}
      
      <main className="relative">
        <div className="relative z-10">
          <Hero />
        </div>
        <div className="relative z-20">
          <SovereignShipment />
        </div>
        <ContactCTA />
      </main>
    </div>
  );
}

export default App;
